import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Grid, Paper, StepButton, StepConnector, Tab, Tabs, useMediaQuery, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Step from '@mui/material/Step';
import StepContent from '@mui/material/StepContent';
import StepLabel from '@mui/material/StepLabel';
import Stepper from '@mui/material/Stepper';
import Typography from '@mui/material/Typography';
import { usePathname, useRouter } from 'next/navigation';
import React, { useEffect } from 'react';

import FurtherAssistanceContact from '../FurtherAssistanceContact';
import { NodeIndexIndicator, TabLabelText } from './FormPage.helpers';

import FormCTAs from '@src/components/FormCTAs';
import FormStepTitle from '@src/components/FormStepTitle';
import CollectionsBlockedDialog from '@src/components/shared/CollectionsBlockedDialog/CollectionsBlockedDialog';
import useCollectionsGuard from '@src/hooks/useCollectionsGuard';
import useRouteGuard from '@src/hooks/useRouteGuard';
import useScrollToTop from '@src/hooks/useScrollToTop';
import { getLogger } from '@src/lib/logging/logger';
import { useClientInformationStore, useRouteHandlerStore } from '@src/store';
import useCollectionsDialogStore from '@src/store/modules/collections-dialog.store';
import { getRouteIsComplete } from '@src/store/modules/route-handler.store';

interface FormPageProps {
	children: React.ReactElement;
	formId: string;
	hideTitle?: boolean;
	loading?: boolean;
	title: string;
	setLoading?: (loading: boolean) => void;
	recaptchaSiteKey?: string;
}

const FormPage = ({
	title,
	children,
	formId,
	hideTitle = false,
	loading,
	setLoading,
	recaptchaSiteKey,
}: FormPageProps) => {
	const logger = getLogger('client');
	useRouteGuard();
	const theme = useTheme();
	const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
	const router = useRouter();
	const pathname = usePathname();
	const resolvedPathname = pathname ?? '';
	const props = { formId, loading };
	const scrollToTop = useScrollToTop();
	const isInCollections = useClientInformationStore((s) => s.isInCollections);
	const openCollectionsDialog = useCollectionsDialogStore((s) => s.openDialog);
	const guard = useCollectionsGuard();

	useEffect(() => {
		if (isInCollections) openCollectionsDialog();
	}, [isInCollections, openCollectionsDialog]);

	useEffect(() => {
		const form = document.getElementById(formId);
		if (!form) return;
		const handler = (e: Event) => {
			if (guard(e)) {
				e.stopImmediatePropagation();
				setLoading?.(false);
			}
		};
		form.addEventListener('submit', handler, true);
		return () => form.removeEventListener('submit', handler, true);
	}, [formId, children, setLoading, guard]);

	// routing logic
	const { routes, routeProgress, navToPriorRoute, setCurrentRouteIndex } = useRouteHandlerStore((store) => store);
	const { currentRoute, previousRoutes, upcomingRoutes } = routeProgress();
	const safeRoutes = Array.isArray(routes) ? routes : [];
	const normalizedPathname = resolvedPathname.endsWith('/') ? resolvedPathname.slice(0, -1) : resolvedPathname;
	const activeRouteValue =
		safeRoutes.find((route) => {
			const normalizedRoute = route.route.endsWith('/') ? route.route.slice(0, -1) : route.route;
			return normalizedPathname === normalizedRoute || normalizedPathname.endsWith(normalizedRoute);
		})?.route ?? false;

	// Browser navigation
	useEffect(() => {
		const pathStr = resolvedPathname;
		const cleanPath = pathStr.endsWith('/') ? pathStr.slice(0, -1) : pathStr;
		const requestedRoute = cleanPath.split('/').pop();
		if (requestedRoute) {
			const idx = safeRoutes.findIndex((r) => r.route.replace(/^\//, '') === requestedRoute);
			if (idx !== -1) setCurrentRouteIndex(idx);
		}
	}, [resolvedPathname, safeRoutes, setCurrentRouteIndex]);

	if (!currentRoute) {
		logger.error('FormPage.tsx: "currentRoute" is undefined');
		router.push('/');
		return null;
	}

	return (
		<Box display="flex" flexDirection="column" position="relative">
			{isSmallScreen ? (
				<Stepper
					sx={{ padding: 2.5 }}
					orientation="vertical"
					activeStep={currentRoute?.index || 0}
					connector={null}
				>
					{/* previously hit routes */}
					{previousRoutes.map((priorRoute) => (
						<Step key={priorRoute.index}>
							<StepButton
								disabled={!upcomingRoutes.length}
								onClick={() => navToPriorRoute(priorRoute.route, (route: string) => router.push(route))}
							>
								<StepLabel>{priorRoute.title}</StepLabel>
							</StepButton>
							<StepConnector />
						</Step>
					))}
					{/* current active route */}
					<Step key={currentRoute?.index}>
						<StepLabel>{currentRoute?.title}</StepLabel>
						<StepContent sx={{ marginX: 0, marginY: 2, paddingX: 0, border: 'none' }}>
							{React.cloneElement(children, props)}
						</StepContent>
					</Step>
					{/* upcoming routes */}
					{upcomingRoutes.map((upcomingRoute) => (
						<Step key={upcomingRoute.index}>
							<StepConnector />
							<StepLabel>{upcomingRoute.title}</StepLabel>
						</Step>
					))}
				</Stepper>
			) : (
				<Box display="flex" justifyContent="center">
					<Grid
						container
						maxWidth="xl"
						direction="row"
						justifyContent="center"
						justifySelf="center"
						spacing={2.5}
						mt={2.5}
						ml={0}
						pl={2.5}
						pr={5}
					>
						<Grid item xs={3}>
							<Paper elevation={0} variant="outlined">
								<Tabs
									orientation="vertical"
									sx={{ '.MuiTabs-indicator': { left: 0, bgcolor: 'secondary.main', width: 4 } }}
									value={activeRouteValue}
								>
									{previousRoutes?.map((priorRoute) => {
										return (
											<Tab
												key={priorRoute.route}
												sx={{
													display: 'flex',
													justifyContent: 'start',
													paddingLeft: { sm: 1, md: 2 },
												}}
												disabled={!upcomingRoutes.length}
												label={<TabLabelText label={priorRoute.title} />}
												value={priorRoute.route}
												icon={
													<CheckCircleIcon fontSize="medium" sx={{ color: 'success.dark' }} />
												}
												iconPosition="start"
												onClick={() => {
													if (setLoading) setLoading(true);
													scrollToTop();
													navToPriorRoute(priorRoute.route, (route: string) =>
														router.push(route)
													);
												}}
											/>
										);
									})}
									<Tab
										sx={{
											display: 'flex',
											justifyContent: 'start',
											paddingLeft: { sm: 1, md: 2 },
											bgcolor: 'backgrounds.light',
										}}
										disabled={!upcomingRoutes.length}
										label={<TabLabelText label={currentRoute?.title ?? ''} active />}
										value={currentRoute?.route}
										icon={
											<Typography color="secondary.main" variant="overline">
												<NodeIndexIndicator index={currentRoute?.displayIndex} active />
											</Typography>
										}
										iconPosition="start"
									/>
									{upcomingRoutes.map((upcomingRoute) => {
										const isCompleted = getRouteIsComplete(upcomingRoute.route);
										return (
											<Tab
												key={upcomingRoute.route}
												sx={{
													display: 'flex',
													justifyContent: 'start',
													paddingLeft: { sm: 1, md: 2 },
												}}
												label={<TabLabelText label={upcomingRoute.title} />}
												value={upcomingRoute.route}
												disabled
												icon={
													isCompleted ? (
														<CheckCircleIcon
															fontSize="medium"
															sx={{ color: 'success.dark' }}
														/>
													) : (
														<Typography color="secondary.main" variant="overline">
															<NodeIndexIndicator index={upcomingRoute.displayIndex} />
														</Typography>
													)
												}
												iconPosition="start"
											/>
										);
									})}
								</Tabs>
							</Paper>
						</Grid>
						<Grid item xs={9}>
							<Paper elevation={0} variant="outlined" sx={{ padding: 5 }}>
								{!hideTitle && upcomingRoutes.length ? (
									<FormStepTitle title={title} titleId={`form-step-title-${title}`} />
								) : null}
								{React.cloneElement(children, props)}
							</Paper>
						</Grid>
					</Grid>
				</Box>
			)}
			{isSmallScreen ? null : (
				<Box textAlign="center">
					<FurtherAssistanceContact />
				</Box>
			)}
			{upcomingRoutes.length > 0 && (
				<FormCTAs formId={formId} setLoading={setLoading} recaptchaSiteKey={recaptchaSiteKey} />
			)}

			{/* Full-page loader overlay */}
			{loading && (
				<Box
					position="fixed"
					top={0}
					left={0}
					width="100vw"
					height="100vh"
					display="flex"
					alignItems="center"
					justifyContent="center"
					bgcolor="rgba(255,255,255,0.7)"
					zIndex={9999}
				>
					<CircularProgress size={60} color="primary" />
				</Box>
			)}
			{isSmallScreen ? (
				<Box bgcolor="backgrounds.white" py={2} textAlign="center">
					<FurtherAssistanceContact />
				</Box>
			) : null}
			<CollectionsBlockedDialog />
		</Box>
	);
};

export default FormPage;
